import { NodeApi, TreeApi } from 'react-arborist';
import { useEditClusterDate, useViewportPageIndexContext } from '../hooks';
import {
  BundleSectionsTreeNode,
  ReorderBundlePagesPasteLocationType,
  SortBundleProviderType,
  useLazyLegacyReorderBundlePagesQuery,
  useReorderBundleProviderMutation,
  useLazySortBundleProviderQuery,
  useRenameBundleSectionsMutation,
  useLazyReorderBundleSectionsQuery,
  useSlotIntoOriginalRecordsMutation,
  useAddToLightBundleMutation,
  useExtractToMutation,
  useLazyGetBundleCategoryListQuery,
  useAddBundlePageCategoryMutation,
  useRemoveFromLightBundleMutation,
  BundleCategoryItem,
  isEndpointLockError,
} from '@/api';
import { useImperativeHandle, useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { CloseIcon } from '@repo/atomic-components';
import { createPortal } from 'react-dom';
import {
  findFirstPageIdInTreeNode,
  findFullNodePathToNodeById,
  findTopLevelTreeNodeByNodeId,
  flattenPageIds,
  removePageIdsFromTree,
  getTreeNodesByNodeIds,
  SectionsTree,
  Toast,
  MenuItem,
  OptionsDropdown,
  OptionsDropdownAnchor,
  OptionsDropdownItem,
  SuggestionItem,
  BundleTreeType,
  BundleTreeTypes,
  ConfirmationDialog,
  confirmDialog,
  moveNodesInTree,
  getTreeNodeKeyByPageId,
  YellowWarningIcon,
  UndoIcon,
  RedoIcon,
  PencilIcon,
  CalendarIcon,
  ExtractIcon,
  MoveToSectionIcon,
  CheckIconV2,
  EyeToSeeIcon,
  CopyIcon,
} from '@repo/atomic-components';
import {
  BundleSectionsTreeStickyPins,
  useStickyAncestorPins,
  useTreeDragAutoscroll,
  useTreeDragHoverExpand,
} from './BundleSectionsTreeStickyPins';
import { isDropForbidden } from './BundleSectionsTree.dragRules';
import { buildIndexClipboardText } from './BundleSectionsTree.copyIndex';
import {
  BundleType,
  makeSelectBundlePageIdByPageIndex,
  makeSelectBundlePageIndexByPageId,
  makeSelectBundlePageDataByPageId,
  mergePageInfo,
  PageInfoData,
  popBusyTask,
  pushBusyTask,
  selectBundleCategoriesList,
  selectBundleCutOutThumbnailIndexes,
  selectBundleMetadataByStoreKey,
  selectBundlePageIds,
  selectBundleSections,
  selectBundleSectionsSuggestionsCategoryList,
  selectBundleSectionsSuggestionsSubCategoryList,
  selectPageInfoCache,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectViewportPageIdForRestorePageIndexInStore,
  selectViewportPageIndexInStore,
  setBundleCutOutThumbnailIndexes,
  setBundlePageIds,
  setBundleSections,
  setViewportPageIdForRestorePageIndexInStore,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { MoveSectionsDialog } from './MoveSectionsDialog';
import { ExtractToPopup } from './ExtractToPopup';
import { EditClusterDateDialog } from './EditClusterDateDialog';
import {
  AccessControlAction,
  AppRoutes,
  AppRoutesSearchParams,
  CATEGORY_SUGGESTIONS,
  TREE_SORT_PROVIDER_CONFIRM,
} from '@/constants';
import { BundleMetadataType } from '@/api';
import { openNewTab } from '@/utils';
import { useSearchParams } from 'react-router';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';
import { useAccessControl } from '@/hooks';
import {
  BROADCAST_FORCE_CATEGORY_LIST_REFRESH,
  BROADCAST_REMOVE_PAGES_FROM_LIGHT_BUNDLE,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
  useFletchersBroadcast,
} from '@/broadcast';

export const BundleSectionsTree: React.FC = () => {
  const toast = useRef<Toast>(null);
  const optimisticDataUpdateRef = useRef<boolean>(false);
  const { sendMessageWithoutResponse, sendMessageWithResponse } = useFletchersBroadcast();
  const dispatch = useAppDispatch();

  const [triggerReorderBundleSections] = useLazyReorderBundleSectionsQuery();
  const [reorderProvider, { isLoading: isReorderingProvider }] = useReorderBundleProviderMutation();
  const [triggerReorderBundlePages] = useLazyLegacyReorderBundlePagesQuery();
  const [triggerBundleProviderSort] = useLazySortBundleProviderQuery();
  const [triggerRenameBundleSections, { error: renameBundleSectionsError, reset: renameBundleSectionsReset }] =
    useRenameBundleSectionsMutation();
  const [slotIntoOriginalRecords] = useSlotIntoOriginalRecordsMutation();
  const [triggerAddToLightBundle] = useAddToLightBundleMutation();
  const [triggerExtractTo] = useExtractToMutation();
  const [triggerGetBundleCategoryList] = useLazyGetBundleCategoryListQuery();
  const [triggerAddBundlePageCategory] = useAddBundlePageCategoryMutation();
  const [triggerRemoveFromLightBundle] = useRemoveFromLightBundleMutation();
  const categoriesList = useAppSelector(selectBundleCategoriesList);
  const currentBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey());
  const otherBundleId = currentBundleMetadata?.otherBundleId;
  const lightBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey(BundleType.LIGHT));
  const fullBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey(BundleType.FULL));
  const [searchParams] = useSearchParams();
  const currentSearchParams = searchParams.toString();

  const isEditBundles = useAccessControl(AccessControlAction.EDIT_BUNDLES);
  const documentReviewPageContext = useViewportPageIndexContext();
  const bundleSections = useAppSelector(selectBundleSections);
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const suggestionsMapWithBundleSectionsCategoryList = useAppSelector(selectBundleSectionsSuggestionsCategoryList);
  const suggestionsMapWithBundleSectionsSubCategoryList = useAppSelector(
    selectBundleSectionsSuggestionsSubCategoryList,
  );
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);

  const bundleCutOutThumbnailIndexes = useAppSelector(selectBundleCutOutThumbnailIndexes);

  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);
  const viewportPageIdForRestorePageIndexInStore = useAppSelector(selectViewportPageIdForRestorePageIndexInStore);

  const getBundlePageIdByPageIndex = useAppSelector(makeSelectBundlePageIdByPageIndex);
  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);
  const getBundlePageDataByPageId = useAppSelector(makeSelectBundlePageDataByPageId);
  const pageInfoCache = useAppSelector(selectPageInfoCache);

  const treeApiRef = useRef<TreeApi<BundleSectionsTreeNode>>(null);
  const [nodeIdRelatedToViewportPageIndex, setNodeIdRelatedToViewportPageIndex] = useState<string | null>(null);
  const [highlightedNodeIdsRelatedToViewportPageIndex, setHighlightedNodeIdsRelatedToViewportPageIndex] = useState<
    string[]
  >([]);

  const [selectedNodes, setSelectedNodes] = useState<BundleSectionsTreeNode[]>([]);
  const [visibleMoveSectionsDialog, setVisibleMoveSectionsDialog] = useState<boolean>(false);
  const [moveSectionsPageIds, setMoveSectionsPageIds] = useState<string[]>([]);

  const [showClusterExtractToPopup, setShowClusterExtractToPopup] = useState<boolean>(false);
  const [showClusterExtractToNewCategoryInput, setShowClusterExtractToNewCategoryInput] = useState<boolean>(false);
  const [clusterExtractPageIds, setClusterExtractPageIds] = useState<string[]>([]);
  const [clusterExtractDate, setClusterExtractDate] = useState<string | null>(null);

  // Edit-date dialog — shared with the thumbnails-bar date pencil.
  const {
    editState: editClusterDateState,
    openForClusters: openEditClusterDate,
    close: closeEditClusterDate,
    confirm: confirmEditClusterDate,
  } = useEditClusterDate();

  const [contextMenuSelectedNode, setContextMenuSelectedNode] = useState<NodeApi<BundleSectionsTreeNode> | null>(null);
  const [optionsDropdownAnchor, setOptionsDropdownAnchor] = useState<OptionsDropdownAnchor | null>(null);

  // Helper function to check if a provider is non-original (duplicate)
  const isNonOriginalProvider = (providerNode: BundleSectionsTreeNode): boolean => {
    if (providerNode.type !== BundleTreeTypes.PROVIDER) return false;

    // Must have a date to be considered non-original
    if (!providerNode.date) return false;

    return true;
  };

  // Helper function to check if moving between original and additional record providers
  const isMixedProviderMoveByProviderIds = (sourceProviderIds: Set<string>, targetProviderId: string): boolean => {
    // Get source providers details
    const sourceProviders = Array.from(sourceProviderIds)
      .map((id) => bundleSections.find((section) => section.id === id))
      .filter((provider): provider is BundleSectionsTreeNode => provider !== undefined);

    // Get target provider details
    const targetProvider = bundleSections.find((section) => section.id === targetProviderId);

    if (!targetProvider || sourceProviders.length === 0) return false;

    // Check if source providers have dates (additional records providers)
    const sourceHasDate = sourceProviders.some((provider) => !!provider.date);
    const sourceHasNoDate = sourceProviders.some((provider) => !provider.date);

    // Check if target provider has date (additional records provider)
    const targetHasDate = !!targetProvider.date;

    // If mixing original (no date) and additional (has date) providers, prevent the move
    if ((sourceHasDate && !targetHasDate) || (sourceHasNoDate && targetHasDate)) {
      return true;
    }

    return false;
  };

  // Helper function to get all page IDs from a provider section
  const getAllPageIdsFromProvider = (providerNode: BundleSectionsTreeNode): string[] => {
    return flattenPageIds([providerNode]);
  };

  // Helper: return the first page id in a node whose pages are in the light bundle (or null)
  const getFirstPageIdInLightBundle = (node: BundleSectionsTreeNode): string | null => {
    for (const id of flattenPageIds([node])) {
      const cached = pageInfoCache[id]?.inLightBundle;
      const inLight = typeof cached === 'boolean' ? cached : getBundlePageDataByPageId(id)?.inLightBundle;
      if (inLight) return id;
    }
    return null;
  };

  const jumpToPageInOtherBundle = (targetBundle: 'light' | 'full', pageId: string) => {
    const broadcast =
      targetBundle === 'light'
        ? BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE
        : BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE;
    const fallbackMetadata = targetBundle === 'light' ? fullBundleMetadata : lightBundleMetadata;
    const fallbackBundleType = targetBundle === 'light' ? BundleMetadataType.FULL : BundleMetadataType.LIGHT;
    const fallbackRoute = targetBundle === 'light' ? AppRoutes.LightBundle : AppRoutes.FullBundle;

    sendMessageWithResponse(broadcast, { showPageIdInViewport: pageId }, 2000)
      .catch(() => {
        if (
          fallbackMetadata &&
          fallbackMetadata.caseId &&
          fallbackMetadata.otherBundleId &&
          fallbackMetadata.bundleType === fallbackBundleType
        ) {
          const newUrl =
            `/${AppRoutes.CaseList}` +
            `/${fallbackMetadata.caseId}` +
            `/${AppRoutes.AiProcessedDocuments}` +
            `/${fallbackRoute}` +
            `/${fallbackMetadata.otherBundleId}` +
            `?${currentSearchParams}&${AppRoutesSearchParams.pageIdInViewportOnInit}=${pageId}`;
          return openNewTab(newUrl);
        }
      })
      .then((response) => {
        if (response instanceof Window) response.focus();
      })
      .catch((error) => {
        if (error instanceof Error) console.info(error.message);
      });
  };

  // Helper: return the subset of a node's pages not yet in the light bundle
  const getNodePageIdsNotInLightBundle = (node: BundleSectionsTreeNode): string[] => {
    return flattenPageIds([node]).filter((id) => {
      const cached = pageInfoCache[id]?.inLightBundle;
      if (typeof cached === 'boolean') return !cached;
      return !getBundlePageDataByPageId(id)?.inLightBundle;
    });
  };

  const isExtractableNode = (node: BundleSectionsTreeNode | undefined): boolean => {
    if (!node) return false;
    if (
      node.type === BundleTreeTypes.CLUSTER ||
      node.type === BundleTreeTypes.CATEGORY ||
      node.type === BundleTreeTypes.SUB_CATEGORY
    ) {
      return true;
    }
    return false;
  };

  // "Extract to..." is broader: also allows the raw document-name nodes that
  // appear after reverting a provider to its original sort.
  const isExtractableToNode = (node: BundleSectionsTreeNode | undefined): boolean => {
    if (!node) return false;
    if (isExtractableNode(node)) return true;
    return node.type === BundleTreeTypes.DOCUMENT;
  };

  // When the clicked cluster is part of the current multi-selection, aggregate
  // every cluster in that selection so the action operates on all of them.
  // Non-cluster clicks (category/sub-category/document/provider) and
  // single-cluster selections fall back to just the clicked node — only
  // clusters aggregate.
  const getSelectedClusterTargets = (): BundleSectionsTreeNode[] => {
    if (!contextMenuSelectedNode) return [];
    const clickedNode = contextMenuSelectedNode.data;
    if (clickedNode.type !== BundleTreeTypes.CLUSTER) return [clickedNode];
    const contextIsInSelection = selectedNodes.some((n) => n.id === contextMenuSelectedNode.id);
    if (!contextIsInSelection) return [clickedNode];
    const clusterSelections = selectedNodes.filter((n) => n.type === BundleTreeTypes.CLUSTER);
    return clusterSelections.length > 1 ? clusterSelections : [clickedNode];
  };

  const openClusterExtractToPopup = () => {
    if (!contextMenuSelectedNode || !bundleId) return;
    const targetNodes = getSelectedClusterTargets();
    if (!targetNodes.length) return;

    const pageIds = targetNodes.flatMap((node) => getNodePageIdsNotInLightBundle(node));
    if (!pageIds.length) return;

    setClusterExtractPageIds(pageIds);
    setClusterExtractDate(null);
    setShowClusterExtractToNewCategoryInput(false);
    triggerGetBundleCategoryList({ bundleId });
    setShowClusterExtractToPopup(true);
  };

  const closeClusterExtractToPopup = () => {
    setShowClusterExtractToPopup(false);
    setShowClusterExtractToNewCategoryInput(false);
    setClusterExtractPageIds([]);
    setClusterExtractDate(null);
  };

  const withBusyTask = async <T,>(idPrefix: string, description: string, work: () => Promise<T>): Promise<T> => {
    const id = `${idPrefix}-${Date.now()}`;
    dispatch(pushBusyTask({ id, description }));
    try {
      return await work();
    } finally {
      dispatch(popBusyTask(id));
    }
  };

  const handleClusterExtractToSubmit = async (category: BundleCategoryItem | string) => {
    if (!bundleId || !clusterExtractPageIds.length) return;

    let categoryName: string | null = null;
    let categoryId: string | null = null;
    let displayName = '';

    if (typeof category === 'string') {
      const existing = categoriesList.find((c) => c.categoryName === category);
      if (existing) {
        categoryId = existing.categoryId;
        displayName = existing.categoryName;
      } else {
        categoryName = category;
        displayName = category;
      }
    } else {
      categoryId = category.categoryId;
      displayName = category.categoryName;
    }

    const pageIds = [...clusterExtractPageIds];
    closeClusterExtractToPopup();

    try {
      await withBusyTask(
        'extract-to',
        `Extracting ${pageIds.length} ${pageIds.length === 1 ? 'page' : 'pages'}...`,
        async () => {
          if (categoryId) {
            await triggerExtractTo({
              bundleId,
              categoryId,
              categoryName: null,
              pageIds,
              date: clusterExtractDate,
            }).unwrap();
          } else if (categoryName) {
            await triggerExtractTo({
              bundleId,
              categoryId: null,
              categoryName,
              pageIds,
              date: clusterExtractDate,
            }).unwrap();
          }

          const cacheUpdates: Record<string, PageInfoData> = {};
          pageIds.forEach((id) => {
            cacheUpdates[id] = { inLightBundle: true };
          });
          dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));

          sendMessageWithResponse(
            BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
            { showPageIdInViewport: pageIds[pageIds.length - 1], bundleId: otherBundleId },
            1000,
          )
            .then((response) => {
              if (response instanceof Window) response.focus();
            })
            .catch((error) => console.info(error.message));

          toast.current?.show({
            severity: 'success',
            summary: 'Extracted',
            detail: `Extracted ${pageIds.length} ${pageIds.length === 1 ? 'page' : 'pages'} to ${displayName}.`,
            life: 3000,
          });
        },
      );
    } catch (error) {
      if (isEndpointLockError(error)) return;
      toast.current?.show({
        severity: 'error',
        summary: 'Extract failed',
        detail: 'Failed to extract pages. Please try again.',
        life: 5000,
      });
    }
  };

  const handleRemoveSectionFromLightBundle = async () => {
    if (!contextMenuSelectedNode || !bundleId) return;

    const targetNodes = getSelectedClusterTargets();
    if (!targetNodes.length) return;

    const pageIds = flattenPageIds(targetNodes);
    if (!pageIds.length) return;

    try {
      await withBusyTask(
        'remove-from-light',
        `Removing ${pageIds.length} ${pageIds.length === 1 ? 'page' : 'pages'}...`,
        async () => {
          await triggerRemoveFromLightBundle({ bundleId, pageIds }).unwrap();

          // Optimistically prune the light bundle's local tree/page-id state.
          const nextSections = removePageIdsFromTree({ tree: bundleSections, pageIds });
          const removedSet = new Set(pageIds);
          const nextPageIds = bundlePageIds.filter((id) => !removedSet.has(id));
          dispatch(setBundleSections({ key: BundleType.LIGHT, data: nextSections }));
          dispatch(setBundlePageIds({ key: BundleType.LIGHT, data: nextPageIds }));

          // Reflect the change in the full bundle's page-info cache (so the
          // "inLightBundle" markers on the full bundle stay in sync).
          const cacheUpdates: Record<string, PageInfoData> = {};
          pageIds.forEach((id) => {
            cacheUpdates[id] = { inLightBundle: false };
          });
          dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));

          // Broadcast to any open full-bundle tab so it also refreshes.
          sendMessageWithoutResponse(BROADCAST_REMOVE_PAGES_FROM_LIGHT_BUNDLE, {
            pageIdsToUpdate: pageIds,
          });

          toast.current?.show({
            severity: 'success',
            summary: 'Removed',
            detail: `Removed ${pageIds.length} ${pageIds.length === 1 ? 'page' : 'pages'} from the Light Bundle.`,
            life: 3000,
          });
        },
      );
    } catch (error) {
      if (isEndpointLockError(error)) return;
      toast.current?.show({
        severity: 'error',
        summary: 'Remove failed',
        detail: 'Failed to remove pages from the Light Bundle. Please try again.',
        life: 5000,
      });
    }
  };

  const handleExtractClusterToLightBundle = async () => {
    if (!contextMenuSelectedNode || !bundleId) return;

    const targetNodes = getSelectedClusterTargets();
    if (!targetNodes.length) return;

    const pageIds = targetNodes.flatMap((node) => getNodePageIdsNotInLightBundle(node));
    if (!pageIds.length) return;

    try {
      await withBusyTask(
        'extract-to-light',
        `Extracting ${pageIds.length} ${pageIds.length === 1 ? 'page' : 'pages'}...`,
        async () => {
          await triggerAddToLightBundle({ bundleId, pageIds }).unwrap();

          const cacheUpdates: Record<string, PageInfoData> = {};
          pageIds.forEach((id) => {
            cacheUpdates[id] = { inLightBundle: true };
          });
          dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));

          sendMessageWithResponse(
            BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
            { showPageIdInViewport: pageIds[pageIds.length - 1], bundleId: otherBundleId },
            1000,
          )
            .then((response) => {
              if (response instanceof Window) response.focus();
            })
            .catch((error) => console.info(error.message));

          toast.current?.show({
            severity: 'success',
            summary: 'Extracted',
            detail: `Added ${pageIds.length} ${pageIds.length === 1 ? 'page' : 'pages'} to the Light Bundle.`,
            life: 3000,
          });
        },
      );
    } catch (error) {
      if (isEndpointLockError(error)) return;
      toast.current?.show({
        severity: 'error',
        summary: 'Extract failed',
        detail: 'Failed to extract pages to the Light Bundle. Please try again.',
        life: 5000,
      });
    }
  };

  // Handle slot into original records
  const handleSlotIntoOriginalRecords = async () => {
    if (!contextMenuSelectedNode || !bundleId) return;

    const selectedProvider = contextMenuSelectedNode.data;
    if (selectedProvider.type !== BundleTreeTypes.PROVIDER) return;

    const pageIds = getAllPageIdsFromProvider(selectedProvider);

    if (pageIds.length === 0) {
      toast.current?.show({
        severity: 'info',
        summary: 'No pages to slot',
        detail: 'This provider section contains no pages to slot into earlier records.',
        life: 3000,
      });
      return;
    }

    const busyTaskId = `slot-into-earlier-${Date.now()}`;
    dispatch(
      pushBusyTask({
        id: busyTaskId,
        description: `Slotting ${pageIds.length} ${pageIds.length === 1 ? 'page' : 'pages'}...`,
      }),
    );

    try {
      await slotIntoOriginalRecords({
        page_ids: pageIds,
        bundle_id: bundleId,
      }).unwrap();

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Successfully slotted ${pageIds.length} page${pageIds.length > 1 ? 's' : ''} into earlier records.`,
        life: 3000,
      });
    } catch (error: unknown) {
      if (isEndpointLockError(error)) return;
      const errorMessage =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data &&
        typeof error.data.message === 'string'
          ? error.data.message
          : 'Failed to slot pages into earlier records. Please try again.';
      toast.current?.show({
        severity: 'error',
        summary: 'Slot Failed',
        detail: errorMessage,
        life: 5000,
      });
    } finally {
      dispatch(popBusyTask(busyTaskId));
    }
  };

  // Copies the whole index as indented plain text, regardless of which row
  // was right-clicked — the action is about the index, not the clicked node.
  const handleCopyIndex = useCallback(async () => {
    const indexText = buildIndexClipboardText(bundleSections);
    if (!indexText) return;

    try {
      await navigator.clipboard.writeText(indexText);
      toast.current?.show({
        severity: 'success',
        summary: 'Index copied',
        detail: 'The index has been copied to your clipboard.',
        life: 3000,
      });
    } catch (error) {
      console.error('Copy index failed:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Copy failed',
        detail: 'The index could not be copied. Please try again.',
        closable: true,
        life: 5000,
      });
    }
  }, [bundleSections]);

  const contextMenuModel = useMemo<MenuItem[]>(() => {
    return [
      {
        label: 'Rename',
        visible:
          contextMenuSelectedNode?.data.type === BundleTreeTypes.CATEGORY ||
          contextMenuSelectedNode?.data.type === BundleTreeTypes.SUB_CATEGORY ||
          contextMenuSelectedNode?.data.type === BundleTreeTypes.DOCUMENT,
        command: () => {
          contextMenuSelectedNode?.edit();
        },
      },
      {
        label: 'Paste before this cluster',
        visible:
          contextMenuSelectedNode?.data.type === BundleTreeTypes.CLUSTER && !!bundleCutOutThumbnailIndexes.length,
        command: () => {
          const cutOutPageIds = bundleCutOutThumbnailIndexes.map((item) => getBundlePageIdByPageIndex(item));

          if (
            activeBundleType &&
            bundleId &&
            contextMenuSelectedNode &&
            contextMenuSelectedNode.id &&
            cutOutPageIds.length
          ) {
            triggerReorderBundlePages({
              bundleId: bundleId,
              targetParentId: contextMenuSelectedNode.id,
              targetIndex: 0,
              pageIds: cutOutPageIds,
              pasteLocation: ReorderBundlePagesPasteLocationType.BEFORE,
            })
              .unwrap()
              .then(() => {
                dispatch(
                  setBundleCutOutThumbnailIndexes({
                    key: activeBundleType,
                    data: [],
                  }),
                );
              });
          }
        },
      },
      {
        label: 'Paste after this cluster',
        visible:
          contextMenuSelectedNode?.data.type === BundleTreeTypes.CLUSTER && !!bundleCutOutThumbnailIndexes.length,
        command: () => {
          const cutOutPageIds = bundleCutOutThumbnailIndexes.map((item) => getBundlePageIdByPageIndex(item));

          if (
            activeBundleType &&
            bundleId &&
            contextMenuSelectedNode &&
            contextMenuSelectedNode.id &&
            cutOutPageIds.length
          ) {
            triggerReorderBundlePages({
              bundleId: bundleId,
              targetParentId: contextMenuSelectedNode.id,
              targetIndex: 0,
              pageIds: cutOutPageIds,
              pasteLocation: ReorderBundlePagesPasteLocationType.AFTER,
            })
              .unwrap()
              .then(() => {
                dispatch(
                  setBundleCutOutThumbnailIndexes({
                    key: activeBundleType,
                    data: [],
                  }),
                );
              });
          }
        },
      },
      {
        label: 'Move to section',
        // Only show for clusters, not for sections (category/sub_category) to prevent invisible sections bug
        visible: contextMenuSelectedNode?.data.type === BundleTreeTypes.CLUSTER,
        command: () => {
          if (!contextMenuSelectedNode) return;
          const contextIsInSelection = selectedNodes.some((node) => node.id === contextMenuSelectedNode.id);
          setMoveSectionsPageIds(
            contextIsInSelection ? flattenPageIds(selectedNodes) : flattenPageIds([contextMenuSelectedNode.data]),
          );
          setVisibleMoveSectionsDialog(true);
        },
      },
      {
        label: 'Revert to original sort',
        visible: contextMenuSelectedNode?.data.type === BundleTreeTypes.PROVIDER,
        command: async () => {
          if (
            bundleId &&
            contextMenuSelectedNode &&
            contextMenuSelectedNode.id &&
            contextMenuSelectedNode.data &&
            contextMenuSelectedNode.data.name
          ) {
            const confirmed = await confirmDialog({
              id: TREE_SORT_PROVIDER_CONFIRM,
              providerName: contextMenuSelectedNode.data.name,
            });

            if (confirmed) {
              const busyTaskId = `sort-provider-original-${Date.now()}`;
              dispatch(pushBusyTask({ id: busyTaskId, description: 'Reverting to original sort...' }));
              try {
                await triggerBundleProviderSort({
                  bundleId: bundleId,
                  providerId: contextMenuSelectedNode.id,
                  sortType: SortBundleProviderType.ORIGINAL,
                }).unwrap();
              } catch (error) {
                if (isEndpointLockError(error)) return;
                toast.current?.show({
                  severity: 'error',
                  summary: 'Sort failed',
                  detail: 'Failed to revert to original sort. Please try again.',
                  life: 5000,
                });
              } finally {
                dispatch(popBusyTask(busyTaskId));
              }
            }
          }
        },
      },
      {
        label: 'Revert to AI sort',
        visible: contextMenuSelectedNode?.data.type === BundleTreeTypes.PROVIDER,
        command: async () => {
          if (
            bundleId &&
            contextMenuSelectedNode &&
            contextMenuSelectedNode.id &&
            contextMenuSelectedNode.data &&
            contextMenuSelectedNode.data.name
          ) {
            const confirmed = await confirmDialog({
              id: TREE_SORT_PROVIDER_CONFIRM,
              providerName: contextMenuSelectedNode.data.name,
            });

            if (confirmed) {
              const busyTaskId = `sort-provider-ai-${Date.now()}`;
              dispatch(pushBusyTask({ id: busyTaskId, description: 'Reverting to AI sort...' }));
              try {
                await triggerBundleProviderSort({
                  bundleId: bundleId,
                  providerId: contextMenuSelectedNode.id,
                  sortType: SortBundleProviderType.AI,
                }).unwrap();
              } catch (error) {
                if (isEndpointLockError(error)) return;
                toast.current?.show({
                  severity: 'error',
                  summary: 'Sort failed',
                  detail: 'Failed to revert to AI sort. Please try again.',
                  life: 5000,
                });
              } finally {
                dispatch(popBusyTask(busyTaskId));
              }
            }
          }
        },
      },
      {
        label: 'Slot into earlier records',
        visible:
          contextMenuSelectedNode?.data.type === BundleTreeTypes.PROVIDER &&
          isNonOriginalProvider(contextMenuSelectedNode.data),
        command: async () => {
          await handleSlotIntoOriginalRecords();
        },
      },
      {
        label: 'Edit date',
        visible: contextMenuSelectedNode?.data.type === BundleTreeTypes.CLUSTER,
        command: async () => {
          if (!contextMenuSelectedNode?.id) return;
          // When the clicked cluster is part of a multi-selection, edit the
          // date for every selected cluster in one go. Otherwise just the
          // clicked cluster (same aggregation rule as Extract / Remove).
          const targetIds = getSelectedClusterTargets().map((n) => n.id);
          openEditClusterDate(
            targetIds.length ? targetIds : [contextMenuSelectedNode.id],
            contextMenuSelectedNode.data.name,
          );
        },
      },
      (() => {
        const targetNodes = getSelectedClusterTargets();
        const extractablePageCount = targetNodes.reduce((sum, n) => sum + getNodePageIdsNotInLightBundle(n).length, 0);
        const totalPageCount = targetNodes.length ? flattenPageIds(targetNodes).length : 0;
        const displayCount = extractablePageCount === 0 ? totalPageCount : extractablePageCount;
        return {
          label: `Extract ${displayCount} ${displayCount === 1 ? 'page' : 'pages'}`,
          visible:
            !!contextMenuSelectedNode &&
            isExtractableNode(contextMenuSelectedNode.data) &&
            activeBundleType === BundleType.FULL,
          disabled: extractablePageCount === 0,
          command: async () => {
            await handleExtractClusterToLightBundle();
          },
        };
      })(),
      (() => {
        const targetNodes = getSelectedClusterTargets();
        const extractablePageCount = targetNodes.reduce((sum, n) => sum + getNodePageIdsNotInLightBundle(n).length, 0);
        const totalPageCount = targetNodes.length ? flattenPageIds(targetNodes).length : 0;
        const displayCount = extractablePageCount === 0 ? totalPageCount : extractablePageCount;
        return {
          label: `Extract ${displayCount} ${displayCount === 1 ? 'page' : 'pages'} to...`,
          visible:
            !!contextMenuSelectedNode &&
            isExtractableToNode(contextMenuSelectedNode.data) &&
            activeBundleType === BundleType.FULL,
          disabled: extractablePageCount === 0,
          command: () => {
            openClusterExtractToPopup();
          },
        };
      })(),
      (() => {
        // View in Light Bundle: enabled on FULL bundle when at least one page in the section is in the light bundle.
        // Hidden on original-records provider rows (the top-level container).
        const isProvider = contextMenuSelectedNode?.data.type === BundleTreeTypes.PROVIDER;
        const targetPageId =
          contextMenuSelectedNode && activeBundleType === BundleType.FULL
            ? getFirstPageIdInLightBundle(contextMenuSelectedNode.data)
            : null;
        return {
          label: 'View in Light Bundle',
          visible: !!contextMenuSelectedNode && activeBundleType === BundleType.FULL && !isProvider,
          disabled: !targetPageId,
          command: () => {
            if (targetPageId) jumpToPageInOtherBundle('light', targetPageId);
          },
        };
      })(),
      (() => {
        // View in Full Bundle: shown on LIGHT bundle for any non-provider row.
        const isProvider = contextMenuSelectedNode?.data.type === BundleTreeTypes.PROVIDER;
        const targetPageId =
          contextMenuSelectedNode && activeBundleType === BundleType.LIGHT
            ? (flattenPageIds([contextMenuSelectedNode.data])[0] ?? null)
            : null;
        return {
          label: 'View in Full Bundle',
          visible: !!contextMenuSelectedNode && activeBundleType === BundleType.LIGHT && !isProvider,
          disabled: !targetPageId,
          command: () => {
            if (targetPageId) jumpToPageInOtherBundle('full', targetPageId);
          },
        };
      })(),
      (() => {
        // Remove X pages: shown on every section in the LIGHT bundle. When the
        // clicked cluster is part of a multi-selection, aggregate the count
        // across every selected cluster (same rule as Extract).
        const targetNodes = getSelectedClusterTargets();
        const pageCount = targetNodes.length ? flattenPageIds(targetNodes).length : 0;
        return {
          label: `Remove ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`,
          visible: activeBundleType === BundleType.LIGHT && !!contextMenuSelectedNode,
          disabled: pageCount === 0,
          command: async () => {
            await handleRemoveSectionFromLightBundle();
          },
        };
      })(),
      {
        label: 'Copy index',
        visible: !!bundleSections.length,
        command: async () => {
          await handleCopyIndex();
        },
      },
    ];
  }, [
    activeBundleType,
    bundleCutOutThumbnailIndexes,
    bundleId,
    bundleSections,
    contextMenuSelectedNode,
    dispatch,
    getBundlePageIdByPageIndex,
    handleCopyIndex,
    handleSlotIntoOriginalRecords,
    isNonOriginalProvider,
    openEditClusterDate,
    renameBundleSectionsReset,
    selectedNodes,
    triggerBundleProviderSort,
    triggerReorderBundlePages,
  ]);

  useEffect(() => {
    if (renameBundleSectionsError) {
      if (!isEndpointLockError(renameBundleSectionsError)) {
        toast.current?.show({
          severity: 'error',
          summary: 'Renaming error',
          detail: 'An item with the same name already exists, try a different name, or choose from the drop-down list.',
          sticky: true,
        });
      }
      renameBundleSectionsReset();
    }
  }, [renameBundleSectionsError, renameBundleSectionsReset]);

  const zeroLevelIdsOfZeroNode = useMemo(() => {
    const result: Record<string, boolean> = {};

    /**
     * The default when loading the page should be that all Provider (level 1) sections are expanded but not child sections.
     */
    bundleSections.forEach((node) => {
      result[node.id] = true;
    });

    return result;
  }, [bundleSections]);

  const onSelectedBundleSectionsTreeNodeId = useCallback(
    (newViewportPageIndex: number) => {
      const pageId = getBundlePageIdByPageIndex(newViewportPageIndex);

      if (typeof pageId === 'string') {
        const nodeId = getTreeNodeKeyByPageId(bundleSections, pageId);

        if (typeof nodeId === 'string') {
          // Dark-green highlight is disabled — only the ancestor chain of
          // the current viewed page (current cluster + parents up to the
          // Provider) is shaded, all in the same light green.
          setNodeIdRelatedToViewportPageIndex(null);

          const path = findFullNodePathToNodeById(nodeId, bundleSections);
          setHighlightedNodeIdsRelatedToViewportPageIndex(path ? path.map((n) => n.id) : []);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getBundlePageIdByPageIndex, setNodeIdRelatedToViewportPageIndex],
  );

  useImperativeHandle(documentReviewPageContext.bundleSectionsTreeRef, () => ({
    setBundleSectionsTreeNodeIdByViewportPageIndex: (newViewportPageIndex: number) => {
      treeApiRef.current?.onBlur();
      treeApiRef.current?.deselectAll();
      onSelectedBundleSectionsTreeNodeId(newViewportPageIndex);
    },
  }));

  useEffect(() => {
    if (viewportPageIndexInStore >= 0) {
      onSelectedBundleSectionsTreeNodeId(viewportPageIndexInStore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleSections]);

  const stickyPins = useStickyAncestorPins<BundleSectionsTreeNode>({
    treeApiRef,
    invalidateKey: bundleSections,
  });

  useTreeDragAutoscroll<BundleSectionsTreeNode>({ treeApiRef });
  useTreeDragHoverExpand<BundleSectionsTreeNode>({
    treeApiRef,
    onExpanded: stickyPins.scheduleRecompute,
  });

  useEffect(() => {
    if (viewportPageIdForRestorePageIndexInStore && optimisticDataUpdateRef.current) {
      optimisticDataUpdateRef.current = false;
      dispatch(setViewportPageIdForRestorePageIndexInStore(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportPageIdForRestorePageIndexInStore]);

  const handleSelect = (nodes: NodeApi<BundleSectionsTreeNode>[]) => {
    setSelectedNodes(nodes.map((node) => node.data));
  };

  const handleActivate = (node: NodeApi<BundleSectionsTreeNode>) => {
    // If the user is building a multi-selection (Ctrl/Shift-click) we
    // don't want to scroll the PDF or trigger the imperative handle's
    // deselectAll — that would wipe the selection they just started. A
    // plain single-row click still scrolls as before.
    if (node.tree.selectedIds.size > 1) return;

    const pageId = findFirstPageIdInTreeNode(node.data);

    if (pageId) {
      const pageIndex = getBundlePageIndexByPageId(pageId);

      if (pageIndex !== -1 && typeof pageIndex === 'number' && pageIndex >= 0) {
        if (viewportPageIndexInStore !== pageIndex) {
          documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(pageIndex, {
            groupedVirtuosoThumbnailsListAlign: 'start',
            skipCallBundleSectionTree: true,
          });
        }
        onSelectedBundleSectionsTreeNodeId(pageIndex);
      }
    }
  };

  const handleMove = async ({
    dragNodes,
    dragIds,
    parentNode,
    parentId,
    index,
  }: {
    dragNodes: NodeApi<BundleSectionsTreeNode>[];
    dragIds: string[];
    parentNode: NodeApi<BundleSectionsTreeNode> | null;
    parentId: string | null;
    index: number;
  }) => {
    if (!dragNodes.length || !dragIds.length || typeof index !== 'number' || !activeBundleType || !bundleId) {
      return;
    }
    /**
     * PROVIDERS MOVING LOGIC
     */
    const isMovingProviders = dragNodes.every((node) => node.data.type === BundleTreeTypes.PROVIDER);
    if (isMovingProviders) {
      if (isReorderingProvider) {
        return;
      }

      // The disableDrop rule permits Providers to be dropped in one edge
      // case where react-arborist reports the parent as another Provider
      // (index === children.length, i.e. right below its last child).
      // Translate that back into a root-level position so the rest of this
      // branch operates on root-level indices as it expects.
      let rootIndex = index;
      if (parentNode && parentNode.data && parentNode.data.type === BundleTreeTypes.PROVIDER) {
        const parentRootIndex = bundleSections.findIndex((section) => section.id === parentNode.id);
        if (parentRootIndex !== -1) {
          rootIndex = parentRootIndex + 1;
        }
      }

      if (rootIndex < 0 || rootIndex > bundleSections.length) {
        return;
      }

      const providerSections = dragIds;
      const invalidIds = providerSections.filter((id) => !bundleSections.some((section) => section.id === id));
      if (invalidIds.length) {
        return;
      }

      // Support dragging multiple selected providers as a block. Preserve
      // their original relative order, remove them all from the list, then
      // re-insert at the drop position. `spliceIndex` accounts for the
      // number of removed rows that sat above the drop slot so a downward
      // drag lands where the user aimed.
      const originalSections = [...bundleSections];
      const draggedIdSet = new Set(dragIds);
      const draggedSections = bundleSections.filter((section) => draggedIdSet.has(section.id));
      const removedAbove = bundleSections.slice(0, rootIndex).filter((section) => draggedIdSet.has(section.id)).length;
      const spliceIndex = rootIndex - removedAbove;

      const updatedSections = bundleSections.filter((section) => !draggedIdSet.has(section.id));
      updatedSections.splice(spliceIndex, 0, ...draggedSections);
      const adjustedIndex = rootIndex;
      dispatch(setBundleSections({ key: activeBundleType, data: updatedSections }));
      optimisticDataUpdateRef.current = true;

      // Progress is shown in the ActionsPanel busy-task strip instead of
      // a floating toast — matches how section/subcat moves already work.
      const providerReorderBusyTaskId = `reorder-provider-${Date.now()}`;
      dispatch(pushBusyTask({ id: providerReorderBusyTaskId, description: 'Reordering providers...' }));
      try {
        const currentPageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);

        await reorderProvider({
          bundleId,
          targetIndex: adjustedIndex,
          providerSections,
        }).unwrap();

        dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));
      } catch (error) {
        console.error('Provider reorder error:', error);
        if (!isEndpointLockError(error)) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to reorder provider',
            life: 3000,
          });
        }
        dispatch(setBundleSections({ key: activeBundleType, data: originalSections }));
        optimisticDataUpdateRef.current = false;
      } finally {
        dispatch(popBusyTask(providerReorderBusyTaskId));
      }
      return;
    }
    // Check if nodes are being moved between different providers
    const sourceProviderIds = new Set(
      dragNodes
        .map((node) => {
          let currentNode: NodeApi<BundleSectionsTreeNode> | null = node;
          while (currentNode && currentNode.data.type !== BundleTreeTypes.PROVIDER) {
            currentNode = currentNode.parent;
          }
          return currentNode?.id || null;
        })
        .filter((id): id is string => id !== null),
    );

    // Find target provider ancestor
    let targetProviderId: string | null | undefined = parentNode?.id;
    let currentNode: NodeApi<BundleSectionsTreeNode> | null = parentNode;
    while (currentNode && currentNode.data.type !== BundleTreeTypes.PROVIDER) {
      currentNode = currentNode.parent;
      if (currentNode) targetProviderId = currentNode.id;
    }
    targetProviderId = currentNode?.id || null;

    if (sourceProviderIds.size > 0 && targetProviderId && !sourceProviderIds.has(targetProviderId)) {
      // Check if moving between original and additional record providers
      if (isMixedProviderMoveByProviderIds(sourceProviderIds, targetProviderId)) {
        if (toast.current) {
          toast.current.show({
            severity: 'warn',
            summary: 'Moving items',
            detail: 'Pages cannot be moved between original providers and additional record providers',
            sticky: false,
            life: 5000,
          });
        } else {
          console.warn('Toast ref not initialized');
        }
        return;
      }

      // General check for moving between different providers
      if (toast.current) {
        toast.current.show({
          severity: 'warn',
          summary: 'Moving items',
          detail: 'Pages cannot be moved between providers',
          sticky: false,
          life: 5000,
        });
      } else {
        console.warn('Toast ref not initialized');
      }
      return;
    }
    if (!parentNode || !parentId) {
      return;
    }

    /**
     * CATEGORY → CATEGORY = CONVERT TO SUB-CATEGORY
     *
     * The generic section-reorder endpoint just moves a node under a new
     * parent; it doesn't reclassify types, so dropping a Category into
     * another Category via that endpoint 500s on the backend. What the
     * user actually wants is "make this category into a sub-category of
     * that one" — the same shape as `bundle/category/add` handles.
     *
     * IDs for a "CONSENT" category vs "CONSENT" sub-category are different
     * on the backend, so we can't reuse the dragged category's id as a
     * sub_category_id. We send the sub_category_name and let the backend
     * look up its own sub-category dictionary — mirrors how rename
     * autocomplete works. If the name already exists in the sub-cat
     * list, resolve to its id explicitly.
     */
    /**
     * SUB-CATEGORY → PROVIDER = PROMOTE TO CATEGORY
     *
     * The mirror of "category into category": dropping a sub-category
     * onto the provider level promotes it to a category. Sub-cat and
     * cat IDs are separate on the backend even for identical names, so
     * we resolve the dragged sub-category's name against the category
     * dictionary (falling back to sending the name and letting the
     * backend allocate a fresh id).
     */
    const isDraggingSubCategoryToProvider =
      dragNodes.every((n) => n.data.type === BundleTreeTypes.SUB_CATEGORY) &&
      parentNode.data?.type === BundleTreeTypes.PROVIDER;
    if (isDraggingSubCategoryToProvider) {
      const providerNode = parentNode.data;

      const pageIds = flattenPageIds(dragNodes.map((n) => n.data));
      if (!pageIds.length) return;

      // The backend ranks `newCategoryIndex` among the provider's existing
      // *categories*, not among all its children (which may include other
      // types). react-arborist's `index` counts every child up to the drop
      // slot, so translate it to a category-only index by counting how
      // many CATEGORY siblings sit above the drop point in the provider's
      // children list.
      const providerChildren = parentNode.data?.children ?? [];
      const draggedIds = new Set(dragNodes.map((n) => n.id));
      let categoryIndex = 0;
      for (let i = 0; i < index && i < providerChildren.length; i++) {
        const child = providerChildren[i];
        if (draggedIds.has(child.id)) continue;
        if (child.type === BundleTreeTypes.CATEGORY) categoryIndex++;
      }

      // Optimistic update: transform each dragged sub-cat into a cat with
      // the same children, then reuse moveNodesInTree to place them in
      // the provider's child list. Restore on failure so the tree
      // doesn't linger in an invalid shape if the backend rejects.
      optimisticDataUpdateRef.current = true;
      const restoreBundleSections = [...bundleSections];
      const currentPageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);
      const transformedDragNodes = dragNodes.map((n) => ({
        ...n.data,
        type: BundleTreeTypes.CATEGORY,
      }));
      const bundleSectionsAfterMove = moveNodesInTree({
        tree: bundleSections,
        dragNodes: transformedDragNodes,
        dragIds: dragIds,
        parentNode: providerNode,
        parentId: providerNode.id,
        index: index,
      });
      dispatch(setBundleSections({ key: activeBundleType, data: bundleSectionsAfterMove }));
      dispatch(setBundlePageIds({ key: activeBundleType, data: flattenPageIds(bundleSectionsAfterMove) }));
      dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));

      const busyTaskId = `promote-subcat-${Date.now()}`;
      dispatch(pushBusyTask({ id: busyTaskId, description: 'Promoting sub-category to category...' }));
      try {
        // One request per dragged sub-cat — the backend endpoint accepts a
        // single category name/id per call, so bundling several sub-cats'
        // pages into one call would merge them into a single category. Fire
        // in parallel (same pattern as the sub-cat → different-category path)
        // so only the final refetch overwrites the optimistic state.
        await Promise.all(
          dragNodes.flatMap((draggedNode, i) => {
            const draggedName = draggedNode.data.name?.trim();
            if (!draggedName) return [];
            const draggedPageIds = flattenPageIds([draggedNode.data]);
            if (!draggedPageIds.length) return [];

            const matchedCategory = suggestionsMapWithBundleSectionsCategoryList.find(
              (item) => item.name.toLocaleLowerCase() === draggedName.toLocaleLowerCase(),
            );

            return [
              triggerAddBundlePageCategory({
                bundleId,
                providerSectionId: providerNode.id,
                categoryId: matchedCategory ? matchedCategory.id : null,
                categoryName: matchedCategory ? null : draggedName,
                newCategoryIndex: categoryIndex + i,
                subCategoryId: null,
                subCategoryName: null,
                newSubCategoryIndex: null,
                pageIds: draggedPageIds,
              }).unwrap(),
            ];
          }),
        );
        sendMessageWithoutResponse(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, {
          reason: 'Sub-category dragged to provider level (promoted to category)',
        });
      } catch (error: unknown) {
        // Rollback optimistic update
        optimisticDataUpdateRef.current = true;
        dispatch(setBundleSections({ key: activeBundleType, data: restoreBundleSections }));
        dispatch(setBundlePageIds({ key: activeBundleType, data: flattenPageIds(restoreBundleSections) }));
        dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));
        if (isEndpointLockError(error)) return;
        const message =
          error &&
          typeof error === 'object' &&
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'message' in error.data &&
          typeof error.data.message === 'string'
            ? error.data.message
            : 'Failed to promote sub-category. Please try again.';
        toast.current?.show({
          severity: 'error',
          summary: 'Move failed',
          detail: message,
          life: 5000,
        });
      } finally {
        dispatch(popBusyTask(busyTaskId));
      }
      return;
    }

    /**
     * SUB-CATEGORY → DIFFERENT CATEGORY = MOVE ACROSS CATEGORIES
     *
     * A pure reorder inside the same parent category flows through the
     * generic reorder path below. But moving a sub-category into a
     * different category in the same provider is a data change (the
     * pages get a new parent category), so we call
     * bundle/category/add — same shape as the cat→cat and subcat→provider
     * paths, just with categoryId = target category, subCategoryId/Name
     * for the sub-category being moved.
     */
    const isDraggingSubCategoryAcrossCategories =
      dragNodes.every((n) => n.data.type === BundleTreeTypes.SUB_CATEGORY) &&
      parentNode.data?.type === BundleTreeTypes.CATEGORY &&
      dragNodes.every((n) => n.parent?.id !== parentId);
    if (isDraggingSubCategoryAcrossCategories) {
      const providerNode = findTopLevelTreeNodeByNodeId(bundleSections, parentId);
      if (!providerNode) return;

      // Sub-category-only index within the target category, mirroring
      // the cat→cat conversion.
      const targetChildren = parentNode.data?.children ?? [];
      const draggedIds = new Set(dragNodes.map((n) => n.id));
      let baseSubCategoryIndex = 0;
      for (let i = 0; i < index && i < targetChildren.length; i++) {
        const child = targetChildren[i];
        if (draggedIds.has(child.id)) continue;
        if (child.type === BundleTreeTypes.SUB_CATEGORY) baseSubCategoryIndex++;
      }

      // Optimistic update.
      optimisticDataUpdateRef.current = true;
      const restoreBundleSections = [...bundleSections];
      const currentPageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);
      const bundleSectionsAfterMove = moveNodesInTree({
        tree: bundleSections,
        dragNodes: dragNodes.map((n) => n.data),
        dragIds: dragIds,
        parentNode: parentNode.data,
        parentId: parentId,
        index: index,
      });
      dispatch(setBundleSections({ key: activeBundleType, data: bundleSectionsAfterMove }));
      dispatch(setBundlePageIds({ key: activeBundleType, data: flattenPageIds(bundleSectionsAfterMove) }));
      dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));

      const busyTaskId = `subcat-across-cats-${Date.now()}`;
      dispatch(pushBusyTask({ id: busyTaskId, description: 'Moving sub-category to new category...' }));
      try {
        // One request per dragged sub-cat — the backend endpoint accepts
        // a single sub-cat name/id per call, so bundling multiple
        // sub-cats' pages into one call would merge them into a single
        // sub-cat under the target. Fire in parallel so only the final
        // refetch overwrites the optimistic state (sequential awaits
        // caused each intermediate refetch to revert the optimistic move
        // before the next call fired).
        await Promise.all(
          dragNodes.flatMap((draggedNode, i) => {
            const draggedName = draggedNode.data.name?.trim();
            if (!draggedName) return [];
            const draggedPageIds = flattenPageIds([draggedNode.data]);
            if (!draggedPageIds.length) return [];

            const matchedSubCategory = suggestionsMapWithBundleSectionsSubCategoryList.find(
              (item) => item.name.toLocaleLowerCase() === draggedName.toLocaleLowerCase(),
            );

            return [
              triggerAddBundlePageCategory({
                bundleId,
                providerSectionId: providerNode.id,
                categoryId: parentId,
                categoryName: null,
                newCategoryIndex: null,
                subCategoryId: matchedSubCategory ? matchedSubCategory.id : null,
                subCategoryName: matchedSubCategory ? null : draggedName,
                newSubCategoryIndex: baseSubCategoryIndex + i,
                pageIds: draggedPageIds,
              }).unwrap(),
            ];
          }),
        );
        sendMessageWithoutResponse(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, {
          reason: 'Sub-category dragged into a different category',
        });
      } catch (error: unknown) {
        optimisticDataUpdateRef.current = true;
        dispatch(setBundleSections({ key: activeBundleType, data: restoreBundleSections }));
        dispatch(setBundlePageIds({ key: activeBundleType, data: flattenPageIds(restoreBundleSections) }));
        dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));
        if (isEndpointLockError(error)) return;
        const message =
          error &&
          typeof error === 'object' &&
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'message' in error.data &&
          typeof error.data.message === 'string'
            ? error.data.message
            : 'Failed to move sub-category. Please try again.';
        toast.current?.show({
          severity: 'error',
          summary: 'Move failed',
          detail: message,
          life: 5000,
        });
      } finally {
        dispatch(popBusyTask(busyTaskId));
      }
      return;
    }

    const isDraggingCategoryIntoCategory =
      dragNodes.every((n) => n.data.type === BundleTreeTypes.CATEGORY) &&
      parentNode.data?.type === BundleTreeTypes.CATEGORY;
    if (isDraggingCategoryIntoCategory) {
      const providerNode = findTopLevelTreeNodeByNodeId(bundleSections, parentId);
      if (!providerNode) return;

      const pageIds = flattenPageIds(dragNodes.map((n) => n.data));
      if (!pageIds.length) return;

      // The backend ranks `newSubCategoryIndex` among the target
      // category's existing sub-categories, not among all its children.
      // Convert react-arborist's raw child index into a sub-cat-only
      // index by counting sub-category siblings above the drop point.
      // A drop directly on the target category header lands at index 0 —
      // top of the sub-cat list — instead of appending at the end.
      const targetChildren = parentNode.data?.children ?? [];
      const draggedIds = new Set(dragNodes.map((n) => n.id));
      let subCategoryIndex = 0;
      for (let i = 0; i < index && i < targetChildren.length; i++) {
        const child = targetChildren[i];
        if (draggedIds.has(child.id)) continue;
        if (child.type === BundleTreeTypes.SUB_CATEGORY) subCategoryIndex++;
      }

      // Optimistic update: transform each dragged category into a
      // sub-category and drop them into the target category via
      // moveNodesInTree. Restore on failure.
      optimisticDataUpdateRef.current = true;
      const restoreBundleSections = [...bundleSections];
      const currentPageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);
      const transformedDragNodes = dragNodes.map((n) => ({
        ...n.data,
        type: BundleTreeTypes.SUB_CATEGORY,
      }));
      const bundleSectionsAfterMove = moveNodesInTree({
        tree: bundleSections,
        dragNodes: transformedDragNodes,
        dragIds: dragIds,
        parentNode: parentNode.data,
        parentId: parentId,
        index: index,
      });
      dispatch(setBundleSections({ key: activeBundleType, data: bundleSectionsAfterMove }));
      dispatch(setBundlePageIds({ key: activeBundleType, data: flattenPageIds(bundleSectionsAfterMove) }));
      dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));

      const busyTaskId = `move-cat-into-cat-${Date.now()}`;
      dispatch(pushBusyTask({ id: busyTaskId, description: 'Nesting category as sub-category...' }));
      try {
        // One request per dragged category — the backend endpoint accepts a
        // single sub-category name/id per call, so bundling several
        // categories' pages into one call would merge them into a single
        // sub-category. Fire in parallel (same pattern as the sub-cat →
        // different-category path) so only the final refetch overwrites the
        // optimistic state. Each dragged category's name is resolved against
        // the sub-category dictionary; category and sub-category IDs are
        // separate on the backend even when the names match.
        await Promise.all(
          dragNodes.flatMap((draggedNode, i) => {
            const draggedName = draggedNode.data.name?.trim();
            if (!draggedName) return [];
            const draggedPageIds = flattenPageIds([draggedNode.data]);
            if (!draggedPageIds.length) return [];

            const matchedSubCategory = suggestionsMapWithBundleSectionsSubCategoryList.find(
              (item) => item.name.toLocaleLowerCase() === draggedName.toLocaleLowerCase(),
            );

            return [
              triggerAddBundlePageCategory({
                bundleId,
                providerSectionId: providerNode.id,
                categoryId: parentId,
                categoryName: null,
                newCategoryIndex: null,
                subCategoryId: matchedSubCategory ? matchedSubCategory.id : null,
                subCategoryName: matchedSubCategory ? null : draggedName,
                newSubCategoryIndex: subCategoryIndex + i,
                pageIds: draggedPageIds,
              }).unwrap(),
            ];
          }),
        );
        sendMessageWithoutResponse(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, {
          reason: 'Category dragged into another category (nested as sub-category)',
        });
      } catch (error: unknown) {
        // Rollback optimistic update
        optimisticDataUpdateRef.current = true;
        dispatch(setBundleSections({ key: activeBundleType, data: restoreBundleSections }));
        dispatch(setBundlePageIds({ key: activeBundleType, data: flattenPageIds(restoreBundleSections) }));
        dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));
        if (isEndpointLockError(error)) return;
        const message =
          error &&
          typeof error === 'object' &&
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'message' in error.data &&
          typeof error.data.message === 'string'
            ? error.data.message
            : 'Failed to nest category. Please try again.';
        toast.current?.show({
          severity: 'error',
          summary: 'Move failed',
          detail: message,
          life: 5000,
        });
      } finally {
        dispatch(popBusyTask(busyTaskId));
      }
      return;
    }

    /**
     * OPTIMISTIC DATA UPDATE
     *
     * One adjustment when the dragged items are all clusters: sub-categories
     * always come first inside a Category, so a drop above them gets pushed
     * down to just after the last sub-cat. Otherwise the drop index is
     * respected literally — no date-sorting.
     */
    let resolvedIndex = index;
    const isClusterOnlyDrag = dragNodes.every((n) => n.data.type === BundleTreeTypes.CLUSTER);
    if (parentNode.data?.type === BundleTreeTypes.CATEGORY && isClusterOnlyDrag) {
      const targetChildren = parentNode.data.children ?? [];
      const draggedIdSet = new Set(dragNodes.map((n) => n.id));
      const subCatCount = targetChildren.filter(
        (c) => !draggedIdSet.has(c.id) && c.type === BundleTreeTypes.SUB_CATEGORY,
      ).length;
      if (index < subCatCount) resolvedIndex = subCatCount;
    }

    optimisticDataUpdateRef.current = true;
    //** Restore values if the server returns an error */
    const restoreBundleSections = [...bundleSections];
    const currentPageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);
    const bundleSectionsAfterMove = moveNodesInTree({
      tree: bundleSections,
      dragNodes: dragNodes.map((n) => n.data),
      dragIds: dragIds,
      parentNode: parentNode.data,
      parentId: parentId,
      index: resolvedIndex,
    });
    dispatch(
      setBundleSections({
        key: activeBundleType,
        data: bundleSectionsAfterMove,
      }),
    );
    dispatch(
      setBundlePageIds({
        key: activeBundleType,
        data: flattenPageIds(bundleSectionsAfterMove),
      }),
    );
    dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));

    // The reorder endpoint prefers neighbour section ids over a raw index:
    // section_before/after_target reference the siblings on either side of
    // the drop (null at the parent's top or bottom). Read them off the
    // optimistic post-move tree, where the dragged block already sits in
    // its final contiguous position under the target parent.
    const targetParentPath = findFullNodePathToNodeById(parentId, bundleSectionsAfterMove);
    const targetParentChildren = targetParentPath?.[targetParentPath.length - 1]?.children ?? [];
    const draggedSectionIdSet = new Set(dragIds);
    const firstDraggedPos = targetParentChildren.findIndex((child) => draggedSectionIdSet.has(child.id));
    let lastDraggedPos = -1;
    for (let i = targetParentChildren.length - 1; i >= 0; i--) {
      if (draggedSectionIdSet.has(targetParentChildren[i].id)) {
        lastDraggedPos = i;
        break;
      }
    }
    const sectionBeforeTarget = firstDraggedPos > 0 ? targetParentChildren[firstDraggedPos - 1].id : null;
    const sectionAfterTarget =
      lastDraggedPos !== -1 && lastDraggedPos < targetParentChildren.length - 1
        ? targetParentChildren[lastDraggedPos + 1].id
        : null;

    const moveBusyTaskId = `move-sections-${Date.now()}`;
    dispatch(pushBusyTask({ id: moveBusyTaskId, description: 'Reordering sections...' }));
    //** After that triggered the useEffect for start components scrolling */
    triggerReorderBundleSections({
      bundleId: bundleId,
      targetParentId: parentId,
      targetIndex: resolvedIndex,
      sectionBeforeTarget,
      sectionAfterTarget,
      childSections: dragIds,
    })
      .unwrap()
      .catch((error: unknown) => {
        //** Rollback optimistic update */
        optimisticDataUpdateRef.current = true;
        dispatch(
          setBundleSections({
            key: activeBundleType,
            data: restoreBundleSections,
          }),
        );
        dispatch(
          setBundlePageIds({
            key: activeBundleType,
            data: flattenPageIds(restoreBundleSections),
          }),
        );
        dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));
        //** After that triggered the useEffect for start components scrolling */
        if (isEndpointLockError(error)) return;
        toast.current?.show({
          severity: 'error',
          summary: 'Move failed',
          detail: 'Failed to move sections. Please try again.',
          life: 5000,
        });
      })
      .finally(() => {
        dispatch(popBusyTask(moveBusyTaskId));
      });
  };

  const handleRename = ({
    nodeId,
    newName,
    type,
  }: {
    nodeId: string;
    newName: string | SuggestionItem;
    type: BundleTreeType;
  }) => {
    if (!bundleId) {
      return;
    }

    const topLevelTreeNode = findTopLevelTreeNodeByNodeId(bundleSections, nodeId);

    if (!topLevelTreeNode) {
      return;
    }

    // The rename autocomplete may serve suggestions from a hardcoded list
    // where `id === name` (not a real backend UUID). Always resolve back to
    // the plain typed name and look it up against the correct backend list
    // for the node type being renamed — sending a hardcoded string as
    // category_id fails UUID parsing, and matching a sub-category against
    // the category list (or vice versa) sends the wrong-typed id.
    const typedName = (typeof newName === 'string' ? newName : newName.name).trim();
    if (!typedName) return;

    const resolveAgainst = (list: { id: string; name: string }[]): string | null => {
      const match = list.find((item) => item.name.toLocaleLowerCase() === typedName.toLocaleLowerCase());
      return match ? match.id : null;
    };

    // Document-name nodes (from "Revert to original sort") don't have a
    // category to rename — treat "rename" as "move all pages into a new
    // section with that name" instead. Resolve the node from the tree by
    // the nodeId being renamed rather than from contextMenuSelectedNode,
    // which can point elsewhere if the selection changed while the inline
    // editor was open.
    const [renamedNode] = getTreeNodesByNodeIds(bundleSections, [nodeId]);
    if (type === BundleTreeTypes.DOCUMENT && renamedNode?.type === BundleTreeTypes.DOCUMENT) {
      const pageIds = flattenPageIds([renamedNode]);
      if (!pageIds.length) return;

      const providerSectionId = topLevelTreeNode.id;

      const matchedCategoryId = resolveAgainst(suggestionsMapWithBundleSectionsCategoryList);
      const targetCategoryId = matchedCategoryId;
      const targetCategoryName = matchedCategoryId ? null : typedName;

      triggerAddBundlePageCategory({
        bundleId: bundleId,
        providerSectionId,
        categoryId: targetCategoryId,
        categoryName: targetCategoryName,
        newCategoryIndex: targetCategoryName ? 0 : null,
        subCategoryId: null,
        subCategoryName: null,
        newSubCategoryIndex: null,
        pageIds,
      })
        .unwrap()
        .then(() => {
          sendMessageWithoutResponse(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, {
            reason: 'Pages moved by renaming a document section',
          });
        })
        .catch((error: unknown) => {
          if (isEndpointLockError(error)) return;
          const message =
            error &&
            typeof error === 'object' &&
            'data' in error &&
            error.data &&
            typeof error.data === 'object' &&
            'message' in error.data &&
            typeof error.data.message === 'string'
              ? error.data.message
              : 'Failed to move pages into a new section. Please try again.';
          toast.current?.show({
            severity: 'error',
            summary: 'Move failed',
            detail: message,
            life: 5000,
          });
        });
      return;
    }

    const isSubCategory = type === BundleTreeTypes.SUB_CATEGORY;

    // Reject if a sibling of the same type already has this name. Categories
    // must be unique within their provider; sub-categories must be unique
    // within their category. Two sub-categories with the same name are
    // allowed under different parent categories.
    const findParentAndSiblings = (
      tree: BundleSectionsTreeNode[],
      targetId: string,
      parentPath: BundleSectionsTreeNode[] = [],
    ): { parent: BundleSectionsTreeNode | null; siblings: BundleSectionsTreeNode[] } | null => {
      for (const n of tree) {
        if (n.id === targetId) {
          return { parent: parentPath[parentPath.length - 1] ?? null, siblings: tree };
        }
        if (n.children?.length) {
          const found = findParentAndSiblings(n.children, targetId, [...parentPath, n]);
          if (found) return found;
        }
      }
      return null;
    };

    const parentInfo = findParentAndSiblings(bundleSections, nodeId);
    if (parentInfo) {
      const collision = parentInfo.siblings.find(
        (sibling) =>
          sibling.id !== nodeId &&
          sibling.type === type &&
          sibling.name.trim().toLocaleLowerCase() === typedName.toLocaleLowerCase(),
      );
      if (collision) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Name already in use',
          detail: isSubCategory
            ? `A sub-category called "${typedName}" already exists here. Try a different name.`
            : `A category called "${typedName}" already exists in this provider. Try a different name.`,
          life: 4000,
        });
        return;
      }
    }

    const matchedId = resolveAgainst(
      isSubCategory ? suggestionsMapWithBundleSectionsSubCategoryList : suggestionsMapWithBundleSectionsCategoryList,
    );

    const newCategoryId = matchedId;
    const newCategoryName = matchedId ? null : typedName;

    triggerRenameBundleSections({
      bundleId: bundleId,
      isSubCategory,
      categoryId: newCategoryId,
      categoryName: newCategoryName,
      /** renamedBundleSections function params */
      bundleSections: bundleSections,
      nodeId: nodeId,
      newCategoryNameLocalStateUsage: typedName,
      newCategoryIdLocalStateUsage: matchedId ?? '',
    })
      .unwrap()
      .then(() => {
        sendMessageWithoutResponse(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, {
          reason: 'New category by renaming an existing category',
        });
      })
      // Failures are reported by the renameBundleSectionsError effect
      .catch(() => {});
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLElement>, node: NodeApi<BundleSectionsTreeNode>) => {
    setContextMenuSelectedNode(node);
    if (e.type === 'contextmenu') {
      // Right-click: anchor the dropdown at the mouse cursor.
      setOptionsDropdownAnchor({ top: e.clientY, left: e.clientX, buttonTop: e.clientY, gap: 0 });
    } else {
      // Ellipsis button click: anchor below the button.
      const rect = e.currentTarget.getBoundingClientRect();
      setOptionsDropdownAnchor({ top: rect.bottom + 8, left: rect.left, buttonTop: rect.top, gap: 8 });
    }
  };

  // Right-clicking the empty space around the rows opens the menu too, so
  // "Copy index" is reachable from anywhere on the index. Rows (and sticky
  // pins) preventDefault in their own handler, so anything already handled
  // by a row is ignored here rather than clearing the clicked node.
  const handleIndexBackgroundContextMenu = (e: React.MouseEvent<HTMLElement>) => {
    if (!isEditBundles || e.defaultPrevented) return;
    e.preventDefault();
    setContextMenuSelectedNode(null);
    setOptionsDropdownAnchor({ top: e.clientY, left: e.clientX, buttonTop: e.clientY, gap: 0 });
  };

  const optionsDropdownItems = useMemo<OptionsDropdownItem[]>(() => {
    const iconByLabel: Record<string, React.ReactNode> = {
      Rename: <PencilIcon />,
      'Move to section': <MoveToSectionIcon />,
      'Slot into earlier records': <MoveToSectionIcon />,
      'Revert to original sort': <UndoIcon />,
      'Revert to AI sort': <RedoIcon />,
      'Edit date': <CalendarIcon width={16} height={16} className="-mx-[1px] !h-4 !w-4" />,
      'View in Light Bundle': <EyeToSeeIcon width={16} height={16} className="-mx-[1px] !h-4 !w-4" />,
      'View in Full Bundle': <EyeToSeeIcon width={16} height={16} className="-mx-[1px] !h-4 !w-4" />,
      'Copy index': <CopyIcon width={16} height={16} className="-mx-[1px] !h-4 !w-4" />,
    };
    const iconForExtract = (disabled: boolean | undefined) => (disabled ? <CheckIconV2 /> : <ExtractIcon />);

    return contextMenuModel
      .filter((item) => item.visible !== false)
      .map((item) => {
        const label = typeof item.label === 'string' ? item.label : '';
        const extractIcon = label.startsWith('Extract') ? iconForExtract(item.disabled) : undefined;
        const removeIcon = label.startsWith('Remove') ? (
          <CloseIcon width={12} height={12} className="!h-[12px] !w-[12px]" />
        ) : undefined;
        return {
          label,
          icon: iconByLabel[label] ?? extractIcon ?? removeIcon,
          visible: item.visible,
          disabled: item.disabled,
          onSelect: () => {
            if (typeof item.command === 'function') {
              item.command({ item, originalEvent: undefined as unknown as React.SyntheticEvent });
            }
          },
        };
      });
  }, [contextMenuModel]);

  return (
    <>
      <Toast ref={toast} />

      {visibleMoveSectionsDialog && !!moveSectionsPageIds.length && (
        <MoveSectionsDialog setVisible={setVisibleMoveSectionsDialog} pageIds={moveSectionsPageIds} />
      )}

      {isEditBundles && (
        <OptionsDropdown
          open={!!optionsDropdownAnchor}
          anchor={optionsDropdownAnchor}
          items={optionsDropdownItems}
          onClose={() => setOptionsDropdownAnchor(null)}
        />
      )}

      {showClusterExtractToPopup &&
        createPortal(
          <div className="pointer-events-none fixed inset-0 z-[1000]">
            <div className="pointer-events-auto absolute right-6 bottom-6 w-80">
              <ExtractToPopup
                categoriesVirtualList={categoriesList.filter((category) => category.inBundles)}
                showNewCategoryAutocomplete={showClusterExtractToNewCategoryInput}
                onClose={closeClusterExtractToPopup}
                onExtract={(category) => {
                  void handleClusterExtractToSubmit(category);
                }}
                onNewCategoryClick={() => setShowClusterExtractToNewCategoryInput(true)}
                onNewCategoryCreate={() => setShowClusterExtractToNewCategoryInput(false)}
                onNewCategoryCancel={() => setShowClusterExtractToNewCategoryInput(false)}
                setExtractedDate={setClusterExtractDate}
              />
            </div>
          </div>,
          document.body,
        )}

      <ConfirmationDialog
        id={TREE_SORT_PROVIDER_CONFIRM}
        style={{
          width: '280px',
          borderRadius: '8px',
          border: '1px solid var(--color-fletchers-gray-110)',
          overflow: 'hidden',
        }}
        contentClassName="!p-0"
        headerClassName="!hidden"
      >
        {({ providerName }, { accept, reject }) => {
          return (
            <div className="relative p-[24px]">
              <button
                type="button"
                aria-label="Close"
                onClick={reject}
                className="absolute top-[24px] right-[24px] cursor-pointer rounded p-1 focus:outline-none"
              >
                <CloseIcon width={12} height={12} />
              </button>
              <div className="mt-[16px] flex flex-col items-center">
                <YellowWarningIcon width={48} height={48} />
                <div className="text-fletchers-black-900 mt-[16px] text-[18px]/[18px] font-[600]">Are you sure?</div>
              </div>
              <div className="mt-[16px]">
                {typeof providerName === 'string' && (
                  <p className="text-center text-[14px]/[20px]">
                    Any changes made to <span className="font-semibold">{providerName}</span> will be lost and the page
                    order will be reset.
                  </p>
                )}
              </div>
              <div className="mt-[24px] flex gap-2">
                <OutlineButton
                  className="!h-[32px] flex-1 !justify-center [&_.p-button-label]:!text-center"
                  onClick={reject}
                >
                  Cancel
                </OutlineButton>
                <GreenButton
                  className="!h-[32px] flex-1 !justify-center [&_.p-button-label]:!text-center"
                  onClick={accept}
                >
                  Confirm
                </GreenButton>
              </div>
            </div>
          );
        }}
      </ConfirmationDialog>
      <div className="relative mt-[10px] h-[calc(100%-10px)] w-full" onContextMenu={handleIndexBackgroundContextMenu}>
        <SectionsTree
          treeApiRef={treeApiRef}
          paddingTop={0}
          paddingBottom={10}
          sections={bundleSections}
          initialOpenState={zeroLevelIdsOfZeroNode}
          manuallyHighlightedLightNodeIds={highlightedNodeIdsRelatedToViewportPageIndex}
          manuallyHighlightedDarkNodeId={nodeIdRelatedToViewportPageIndex}
          onSelect={handleSelect}
          onActivate={handleActivate}
          {...(isEditBundles && {
            onMove: handleMove,
            onRename: handleRename,
            onContextMenu: handleContextMenu,
            disableDropRule: ({ dragNodes, parentNode, index }) => isDropForbidden(dragNodes, parentNode, index),
            suggestionsMap: {
              category: CATEGORY_SUGGESTIONS.map((name) => ({ id: name, name })),
              sub_category: CATEGORY_SUGGESTIONS.map((name) => ({ id: name, name })),
            },
          })}
        />
        <BundleSectionsTreeStickyPins
          pins={stickyPins.pins}
          scrollbarWidth={stickyPins.scrollbarWidth}
          overlayRef={stickyPins.overlayRef}
          showKebab={isEditBundles}
          manuallyHighlightedLightNodeIds={highlightedNodeIdsRelatedToViewportPageIndex}
          manuallyHighlightedDarkNodeId={nodeIdRelatedToViewportPageIndex}
          onActivate={handleActivate}
          onOpenContextMenu={handleContextMenu}
          onAfterToggle={stickyPins.scheduleRecompute}
        />
      </div>

      {/* Edit-date dialog — same picker the thumbnails-bar date pencil opens */}
      {editClusterDateState && (
        <EditClusterDateDialog
          key={editClusterDateState.openId}
          initialDate={editClusterDateState.initialDate}
          onCancel={closeEditClusterDate}
          onConfirm={confirmEditClusterDate}
        />
      )}
    </>
  );
};
